import { useSearchParams } from 'react-router-dom'
import { useCallback, useEffect } from 'react'

import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { getErrorMessage, isFailed, isSuccess } from 'redux/toolkit/api'
import { oauth2CreateSession } from 'redux/features/auth/authApiThunks'
import { resetTokenExchange, tokenExchange } from 'redux/features/oauth2/oauth2Slice'
import routesConfig from 'lib/routesConfig'
import { OAuth2TokenExchangeResponse } from 'redux/features/oauth2/oauth2ApiThunks'
import appConfig from 'config/appConfig'
import { usePostAuthCommands } from 'lib/postAuthCommands/usePostAuthCommands'
import { IdToken, IdTokenType } from 'types/oauth2'
import { getLoginAppSessionEndpoint } from 'lib/oauth2/getSessionEndpoint'
import { getAuthProcessId, getCorrelationId } from './loginAppUtils'

/**
 * Check if the token is a Login App admin token.
 */
const isLoginAppToken = (idToken: IdToken): boolean => idToken.type === IdTokenType.LOGIN_APP

/**
 * Submit Login App session via FORM POST.
 *
 * Login App tokens always use FORM POST regardless of region because:
 * 1. Session cookie must be set by browser (not XHR)
 * 2. APIGW returns redirect chain: /session -> /complete -> entry_path
 *
 * The AZP in the token determines which region's APIGW handles the session.
 * auth_process_id and correlation_id are sent as separate form fields for TDF
 * correlation logging (available even if JWT decode fails on TDF side).
 */
const submitLoginAppSession = (res: OAuth2TokenExchangeResponse): void => {
  // Guard: azp is required for Login App tokens - redirect to login on failure
  if (!res.idToken.azp) {
    routesConfig.LOGIN.goto(undefined, false, '?error=LoginAppTokenExchangeFailed')
    return
  }

  const form = document.createElement('form')
  form.method = 'post'
  form.action = getLoginAppSessionEndpoint(res.idToken.azp)
  form.style.display = 'none'

  const idTokenInput = document.createElement('input')
  idTokenInput.name = 'id_token_jwt'
  idTokenInput.value = res.idTokenJwt
  form.appendChild(idTokenInput)

  const authProcessIdInput = document.createElement('input')
  authProcessIdInput.name = 'auth_process_id'
  authProcessIdInput.value = getAuthProcessId(res.idToken)
  form.appendChild(authProcessIdInput)

  // X2: TDF-generated correlation ID for pass check circuit breaker
  const correlationIdInput = document.createElement('input')
  correlationIdInput.name = 'correlation_id'
  correlationIdInput.value = getCorrelationId(res.idToken)
  form.appendChild(correlationIdInput)

  document.body.appendChild(form)
  form.submit()
}

export const useOAuth2CallbackLogic = () => {
  const dispatch = useAppDispatch()
  const [params] = useSearchParams()
  const code = params.get('code')
  const error = params.get('error')
  const { commandFactory, commandHandler } = usePostAuthCommands()

  const {
    isTokenExchangeSuccess,
    isTokenExchangeFailed,
    tokenExchangeError,
    tokenExchangeResponse,
    isOAuth2CreateSessionSuccess,
    isOAuth2CreateSessionFailed
  } = useAppSelector(_store => ({
    isTokenExchangeSuccess: isSuccess(_store.oauth2.api.tokenExchangeApiStatus),
    isTokenExchangeFailed: isFailed(_store.oauth2.api.tokenExchangeApiStatus),
    tokenExchangeError: getErrorMessage(_store.oauth2.api.tokenExchangeApiStatus),
    tokenExchangeResponse: _store.oauth2.tokenExchangeResponse,
    isOAuth2CreateSessionSuccess: isSuccess(_store.auth.api.oauth2CreateSessionApiStatus),
    isOAuth2CreateSessionFailed: isFailed(_store.auth.api.oauth2CreateSessionApiStatus)
  }))

  const getFormActionUrl = useCallback((res: OAuth2TokenExchangeResponse) => {
    if (res.identity.isReactEnabled) {
      return `${res.idToken.azp}/internal/oauth2/session`
    }
    return `${res.idToken.azp}/oauth2/session`
  }, [])

  useEffect(
    () => () => {
      dispatch(resetTokenExchange())
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  // bfcache restoration: page state is stale, code already used - redirect to login
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        routesConfig.LOGIN.goto(undefined, false, '?error=session_expired')
      }
    }
    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [])

  useEffect(() => {
    if (code) {
      dispatch(tokenExchange({ code }))
      return
    }
    if (error) {
      routesConfig.LOGIN.goto(undefined, false, `?error=${error}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isTokenExchangeSuccess && tokenExchangeResponse) {
      // Login App tokens always use FORM POST (session + redirect flow)
      if (isLoginAppToken(tokenExchangeResponse.idToken)) {
        submitLoginAppSession(tokenExchangeResponse)
        return
      }

      // EGD end-user: same region uses JSON POST via Redux
      if (tokenExchangeResponse.idToken.azp === appConfig.GATEWAY_URL) {
        dispatch(oauth2CreateSession({ idTokenJwt: tokenExchangeResponse.idTokenJwt }))
        return
      }

      // EGD end-user: cross-region uses FORM POST
      const wrap = document.createElement('div')
      wrap.style.display = 'none'
      const form = document.createElement('form')
      form.method = 'post'
      form.action = getFormActionUrl(tokenExchangeResponse)
      const inp = document.createElement('input')
      inp.name = 'id_token_jwt'
      inp.value = tokenExchangeResponse.idTokenJwt
      form.appendChild(inp)
      wrap.appendChild(form)
      document.body.appendChild(wrap)
      form.submit()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTokenExchangeSuccess])

  useEffect(() => {
    if (isTokenExchangeFailed) {
      const errorCode = tokenExchangeError || 'oauth2.auth_code_flow.token_exchange_failed'
      routesConfig.LOGIN.goto(undefined, false, `?error=${errorCode}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTokenExchangeFailed])

  useEffect(() => {
    if (isOAuth2CreateSessionSuccess) {
      const command = commandFactory.create(tokenExchangeResponse?.idToken.metadata?.queryString || '')
      commandHandler.handle(command)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOAuth2CreateSessionSuccess])

  useEffect(() => {
    if (isOAuth2CreateSessionFailed) {
      routesConfig.LOGIN.goto(undefined, false, '?error=EGDIdentityNotFound')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOAuth2CreateSessionFailed])
}
